import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LockIcon, UserIcon, MailIcon } from 'lucide-react';
import { adminLogin, adminPasswordReset, forgotPassword, confirmForgotPassword } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import bouncedadsLogo from '../../images/bouncedads1.png';
import { AdminCredentials } from '../../types';

const AdminLogin: React.FC = () => {
  const [credentials, setCredentials] = useState<AdminCredentials>({
    username: '',
    password: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);
  const [requiresForgotPassword, setRequiresForgotPassword] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'newPassword') {
      setNewPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else if (name === 'confirmationCode') {
      setConfirmationCode(value);
    } else {
      setCredentials(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleForgotPassword = async () => {
    if (!credentials.username) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await forgotPassword(credentials.username);
      setForgotPasswordSent(true);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmForgotPassword = async () => {
    if (!confirmationCode || !newPassword) {
      setError('Please enter the confirmation code and new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const result = await confirmForgotPassword(credentials.username, confirmationCode, newPassword);
      // After successful password reset, try to login
      const authResult = await adminLogin(credentials);
      login(authResult.id_token, {
        username: credentials.username,
        email: credentials.username,
        groups: ['admin']
      });
      navigate('/admin/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (requiresForgotPassword) {
        // Handle forgot password confirmation
        await handleConfirmForgotPassword();
        return;
      }

      if (requiresPasswordReset) {
        // Handle password reset
        if (newPassword !== confirmPassword) {
          setError('New passwords do not match');
          return;
        }
        
        if (newPassword.length < 8) {
          setError('Password must be at least 8 characters long');
          return;
        }

        const resetCredentials = {
          username: credentials.username,
          password: credentials.password,
          new_password: newPassword
        };

        const authResult = await adminPasswordReset(resetCredentials);
        login(authResult.id_token, {
          username: credentials.username,
          email: credentials.username,
          groups: ['admin'] // We know they're admin if they passed the password reset
        });
        navigate('/admin/dashboard');
      } else {
        // Handle normal login
        const authResult = await adminLogin(credentials);
        login(authResult.id_token, {
          username: credentials.username,
          email: credentials.username,
          groups: ['admin'] // We know they're admin if they passed the login
        });
        navigate('/admin/dashboard');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Invalid credentials';
      
      // Check if password reset is required
      if (errorMessage.includes('Password reset required') && errorMessage.includes('temporary')) {
        setRequiresForgotPassword(true);
        setError('Please use the forgot password flow to reset your password');
      } else if (errorMessage.includes('Password reset required') && errorMessage.includes('force')) {
        setRequiresPasswordReset(true);
        setError('Please set a new password to continue');
      } else if (errorMessage.includes('Password reset required')) {
        // Default to force password reset if message is ambiguous
        setRequiresPasswordReset(true);
        setError('Please set a new password to continue');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-lg bg-blue-100 mb-4">
            <img 
              src={bouncedadsLogo} 
              alt="Bounce Dads Logo" 
              className="h-12 w-12 object-contain"
            />
          </div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Admin Sign In
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to manage your bounce house rental business
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="email"
                  required
                  value={credentials.username}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your admin email"
                  disabled={loading}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Current Password
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <LockIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={credentials.password}
                  onChange={handleChange}
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>

            {requiresForgotPassword && (
              <>
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Reset Your Password</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {!forgotPasswordSent 
                      ? "Enter your email to receive a password reset code."
                      : "Check your email for the reset code and enter it below."
                    }
                  </p>
                  
                  <div className="space-y-4">
                    {!forgotPasswordSent ? (
                      <div>
                        <button
                          type="button"
                          onClick={handleForgotPassword}
                          disabled={loading}
                          className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {loading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending code...
                            </>
                          ) : (
                            'Send Reset Code'
                          )}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div>
                          <label htmlFor="confirmationCode" className="block text-sm font-medium text-gray-700">
                            Confirmation Code
                          </label>
                          <div className="mt-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <MailIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              id="confirmationCode"
                              name="confirmationCode"
                              type="text"
                              required
                              value={confirmationCode}
                              onChange={handleChange}
                              className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                              placeholder="Enter confirmation code"
                              disabled={loading}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                            New Password
                          </label>
                          <div className="mt-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <LockIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              id="newPassword"
                              name="newPassword"
                              type="password"
                              required
                              value={newPassword}
                              onChange={handleChange}
                              className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                              placeholder="Enter new password (min 8 characters)"
                              disabled={loading}
                            />
                          </div>
                        </div>
                        
                        <div>
                          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                            Confirm New Password
                          </label>
                          <div className="mt-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <LockIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              id="confirmPassword"
                              name="confirmPassword"
                              type="password"
                              required
                              value={confirmPassword}
                              onChange={handleChange}
                              className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                              placeholder="Confirm new password"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {requiresPasswordReset && (
              <>
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Set New Password</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                        New Password
                      </label>
                      <div className="mt-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="newPassword"
                          name="newPassword"
                          type="password"
                          required
                          value={newPassword}
                          onChange={handleChange}
                          className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                          placeholder="Enter new password (min 8 characters)"
                          disabled={loading}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                        Confirm New Password
                      </label>
                      <div className="mt-1 relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <LockIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          required
                          value={confirmPassword}
                          onChange={handleChange}
                          className="appearance-none relative block w-full pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                          placeholder="Confirm new password"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || (requiresForgotPassword && !forgotPasswordSent)}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {requiresForgotPassword ? 'Resetting password...' : requiresPasswordReset ? 'Setting password...' : 'Signing in...'}
                </>
              ) : (
                requiresForgotPassword ? 'Reset Password & Sign In' : requiresPasswordReset ? 'Set Password & Sign In' : 'Sign in'
              )}
            </button>
          </div>
        </form>
        
        <div className="text-center">
          <a
            href="/"
            className="text-blue-600 hover:text-blue-500 text-sm font-medium"
          >
            ← Back to public site
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
